import type { BadgeTone } from '@/components/ui';
import type {
  AlertStatus,
  GlucoseContext,
  HomeReading,
  OutOfRange,
  OwnThresholds,
  ReadingKind,
  ReadingSource,
  ThresholdKey,
  Thresholds,
} from './types';

export const KIND_LABEL: Record<ReadingKind, string> = {
  blood_pressure: 'Blood pressure',
  glucose: 'Glucose',
};

export const OUT_OF_RANGE_LABEL: Record<OutOfRange, string> = {
  bp_high: 'Very high BP',
  glucose_low: 'Low glucose',
  glucose_high: 'Very high glucose',
};

export const ALERT_STATUS_LABEL: Record<AlertStatus, string> = {
  open: 'Open',
  acknowledged: 'Acknowledged',
  resolved: 'Resolved',
};
export const ALERT_STATUS_TONE: Record<AlertStatus, BadgeTone> = {
  open: 'danger',
  acknowledged: 'warning',
  resolved: 'neutral',
};

export const SOURCE_LABEL: Record<ReadingSource, string> = {
  manual: 'Typed in',
  apple_health: 'Apple Health',
  health_connect: 'Health Connect',
  bluetooth: 'Bluetooth device',
};

const CONTEXT_LABEL: Record<GlucoseContext, string> = {
  fasting: 'fasting',
  random: 'random',
  after_meal: 'after a meal',
};

/** "184/102 mmHg · pulse 88" or "3.4 mmol/L · fasting". */
export function readingValue(r: HomeReading): string {
  if (r.kind === 'blood_pressure')
    return `${r.systolic ?? '—'}/${r.diastolic ?? '—'} mmHg${
      r.pulse !== null ? ` · pulse ${r.pulse}` : ''
    }`;
  return `${r.glucoseMmol ?? '—'} mmol/L${
    r.glucoseContext ? ` · ${CONTEXT_LABEL[r.glucoseContext]}` : ''
  }`;
}

/** The API's THRESHOLD_LIMITS (home-reading-rules.ts) and field rules. */
export const THRESHOLD_FIELDS: {
  key: ThresholdKey;
  label: string;
  unit: string;
  min: number;
  max: number;
  /** Whole mmHg for blood pressure; glucose takes one decimal place. */
  integer: boolean;
  help: string;
}[] = [
  {
    key: 'bpSystolicHigh',
    label: 'Systolic high',
    unit: 'mmHg',
    min: 120,
    max: 220,
    integer: true,
    help: 'Alert at or above this systolic.',
  },
  {
    key: 'bpDiastolicHigh',
    label: 'Diastolic high',
    unit: 'mmHg',
    min: 70,
    max: 140,
    integer: true,
    help: 'Alert at or above this diastolic.',
  },
  {
    key: 'glucoseLow',
    label: 'Glucose low',
    unit: 'mmol/L',
    min: 2.5,
    max: 6,
    integer: false,
    help: 'Alert below this glucose.',
  },
  {
    key: 'glucoseHigh',
    label: 'Glucose high',
    unit: 'mmol/L',
    min: 8,
    max: 30,
    integer: false,
    help: 'Alert above this glucose.',
  },
];

export type ThresholdDraft = Record<ThresholdKey, string>;

export function draftOf(own: OwnThresholds): ThresholdDraft {
  const s = (v: number | null) => (v === null ? '' : String(v));
  return {
    bpSystolicHigh: s(own.bpSystolicHigh),
    bpDiastolicHigh: s(own.bpDiastolicHigh),
    glucoseLow: s(own.glucoseLow),
    glucoseHigh: s(own.glucoseHigh),
  };
}

/**
 * Client-side copy of the API's checks (ReadingThresholdsDto and
 * HomeReadingsService.saveThresholds): empty = clinic default (null);
 * blood pressure in whole mmHg, glucose with at most one decimal place,
 * each within its range, and systolic above diastolic (defaults filling
 * in for empty values).
 */
export function validateThresholds(
  draft: ThresholdDraft,
  defaults: Thresholds,
): { values: OwnThresholds; errors: Partial<Record<ThresholdKey, string>> } {
  const values = {} as OwnThresholds;
  const errors: Partial<Record<ThresholdKey, string>> = {};
  for (const f of THRESHOLD_FIELDS) {
    const raw = draft[f.key].trim();
    if (!raw) {
      values[f.key] = null;
      continue;
    }
    const n = Number(raw);
    values[f.key] = null;
    if (!/^\d+(\.\d+)?$/.test(raw) || !Number.isFinite(n))
      errors[f.key] = 'Enter a number.';
    else if (f.integer && !Number.isInteger(n))
      errors[f.key] = 'Enter a whole number.';
    else if (!f.integer && !/^\d+(\.\d)?$/.test(raw))
      errors[f.key] = 'Use at most one decimal place.';
    else if (n < f.min || n > f.max)
      errors[f.key] = `Between ${f.min} and ${f.max} ${f.unit}.`;
    else values[f.key] = n;
  }
  if (!errors.bpSystolicHigh && !errors.bpDiastolicHigh) {
    const sys = values.bpSystolicHigh ?? defaults.bpSystolicHigh;
    const dia = values.bpDiastolicHigh ?? defaults.bpDiastolicHigh;
    if (sys <= dia) errors.bpSystolicHigh = 'Systolic must be above diastolic.';
  }
  return { values, errors };
}
